import React, { useState, useEffect } from 'react';
import { 
  Settings,
  User,
  Bell,
  Shield,
  CreditCard,
  Globe,
  Save,
  Eye,
  EyeOff,
  CheckCircle,
  AlertCircle,
  Info,
  Mail,
  Phone,
  MapPin,
  Calendar,
  DollarSign,
  Percent,
  Clock
} from 'lucide-react';
import { ToastContext } from '../ToastProvider';
import { db } from '../../lib/firebase';
import { doc, getDoc, updateDoc } from 'firebase/firestore';

export default function AffiliateSettings({ affiliateId, userId }) {
  const { notify } = React.useContext(ToastContext);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [settings, setSettings] = useState({
    profile: {
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      birthDate: '',
      profileImage: ''
    },
    notifications: {
      emailNotifications: true,
      smsNotifications: false,
      pushNotifications: true,
      commissionAlerts: true,
      tierUpdates: true,
      payoutAlerts: true,
      marketingEmails: false
    },
    privacy: {
      profileVisibility: 'public',
      showEarnings: false,
      showReferrals: true,
      allowDirectMessages: true,
      dataSharing: false
    },
    payout: {
      preferredMethod: 'gcash',
      gcashNumber: '',
      paymayaNumber: '',
      bankAccount: '',
      bankName: '',
      accountName: '',
      minimumPayout: 500.00,
      autoPayout: false
    },
    preferences: {
      timezone: 'Asia/Manila',
      currency: 'PHP',
      language: 'en',
      theme: 'light',
      dashboardLayout: 'default'
    }
  });

  useEffect(() => {
    loadSettings();
  }, [affiliateId]);

  const loadSettings = async () => {
    if (!affiliateId) return;
    setLoading(true);
    try {
      const ref = doc(db, 'affiliates', affiliateId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        const data = snap.data();
        setSettings(prev => ({
          ...prev,
          profile: {
            firstName: data.userInfo?.firstName || '',
            lastName: data.userInfo?.lastName || '',
            email: data.userInfo?.email || '',
            phone: data.userInfo?.phone || '',
            address: data.userInfo?.address || '',
            birthDate: data.userInfo?.birthDate || ''
          },
          notifications: {
            emailNotifications: !!data.preferences?.notifications,
            smsNotifications: false,
            pushNotifications: true,
            commissionAlerts: true,
            tierUpdates: true,
            payoutAlerts: true,
            marketingEmails: false
          },
            privacy: {
            profileVisibility: 'public',
            showEarnings: false,
            showReferrals: true,
            allowDirectMessages: true,
            dataSharing: false
          },
          payout: {
            preferredMethod: data.payout?.preferredMethod || 'gcash',
            gcashNumber: data.payout?.gcashNumber || '',
            paymayaNumber: data.payout?.paymayaNumber || '',
            bankAccount: data.payout?.bankAccount || '',
            bankName: data.payout?.bankName || '',
            accountName: data.payout?.accountName || '',
            minimumPayout: data.payout?.minimumPayout || 500,
            autoPayout: !!data.payout?.autoPayout
          },
          preferences: {
            timezone: 'Asia/Manila',
            currency: 'PHP',
            language: 'en',
            theme: 'light',
            dashboardLayout: 'default'
          }
        }));
      } else {
        notify('Affiliate document not found', { type: 'error' });
      }
    } catch (error) {
      console.error('Error loading settings:', error);
      notify('Failed to load settings', { type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!affiliateId) return;
    setSaving(true);
    try {
      const ref = doc(db, 'affiliates', affiliateId);
      // Basic sanitization / trimming
      const trim = (v) => (typeof v === 'string' ? v.trim() : v || '');

      const payoutUpdate = {
        preferredMethod: settings.payout.preferredMethod,
        gcashNumber: trim(settings.payout.gcashNumber),
        paymayaNumber: trim(settings.payout.paymayaNumber),
        bankAccount: trim(settings.payout.bankAccount),
        bankName: trim(settings.payout.bankName),
        accountName: trim(settings.payout.accountName),
        minimumPayout: Number(settings.payout.minimumPayout || 500),
        autoPayout: !!settings.payout.autoPayout
      };

      // Build userInfo block from profile section
      const userInfoUpdate = {
        firstName: trim(settings.profile.firstName),
        lastName: trim(settings.profile.lastName),
        email: trim(settings.profile.email),
        phone: trim(settings.profile.phone),
        address: trim(settings.profile.address),
        birthDate: trim(settings.profile.birthDate)
      };

      // Map current UI notification + payout prefs into preferences object
      const preferencesUpdate = {
        notifications: !!settings.notifications.emailNotifications, // legacy single boolean support
        autoPayout: !!settings.payout.autoPayout,
        payoutMethod: settings.payout.preferredMethod,
        payoutThreshold: Number(settings.payout.minimumPayout || 500),
        timezone: settings.preferences.timezone,
        currency: settings.preferences.currency,
        language: settings.preferences.language,
        theme: settings.preferences.theme
      };

      const toUpdate = {
        payout: payoutUpdate,
        userInfo: userInfoUpdate,
        preferences: preferencesUpdate,
        updatedAt: new Date()
      };

      await updateDoc(ref, toUpdate);
      notify('Settings saved successfully!', { type: 'success' });
      // Reload from Firestore to reflect canonical stored shape
      await loadSettings();
    } catch (error) {
      console.error('Error saving settings:', error);
      notify('Failed to save settings', { type: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const updateSettings = (section, field, value) => {
    setSettings(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-PH', {
      style: 'currency',
      currency: 'PHP'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Affiliate Settings</h1>
          <p className="text-gray-600">Manage your affiliate account preferences</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          <span>{saving ? 'Saving...' : 'Save Changes'}</span>
        </button>
      </div>

      {/* Profile Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <User className="w-6 h-6 text-blue-600" />
          <h2 className="text-lg font-semibold text-gray-900">Profile Information</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input
              type="text"
              value={settings.profile.firstName}
              onChange={(e) => updateSettings('profile', 'firstName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input
              type="text"
              value={settings.profile.lastName}
              onChange={(e) => updateSettings('profile', 'lastName', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
            <input
              type="email"
              value={settings.profile.email}
              onChange={(e) => updateSettings('profile', 'email', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
            <input
              type="tel"
              value={settings.profile.phone}
              onChange={(e) => updateSettings('profile', 'phone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
            <textarea
              value={settings.profile.address}
              onChange={(e) => updateSettings('profile', 'address', e.target.value)}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Birth Date</label>
            <input
              type="date"
              value={settings.profile.birthDate}
              onChange={(e) => updateSettings('profile', 'birthDate', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>
      </div>

      {/* Notification Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Bell className="w-6 h-6 text-green-600" />
          <h2 className="text-lg font-semibold text-gray-900">Notification Preferences</h2>
        </div>

        <div className="space-y-4">
          {Object.entries(settings.notifications).map(([key, value]) => (
            <div key={key} className="flex items-center justify-between">
              <div>
                <div className="font-medium text-gray-900">
                  {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                </div>
                <div className="text-sm text-gray-600">
                  {key === 'emailNotifications' && 'Receive notifications via email'}
                  {key === 'smsNotifications' && 'Receive notifications via SMS'}
                  {key === 'pushNotifications' && 'Receive push notifications'}
                  {key === 'commissionAlerts' && 'Get notified when commissions are earned'}
                  {key === 'tierUpdates' && 'Get notified about tier progression'}
                  {key === 'payoutAlerts' && 'Get notified about payout status'}
                  {key === 'marketingEmails' && 'Receive marketing and promotional emails'}
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={value}
                  onChange={(e) => updateSettings('notifications', key, e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>
          ))}
        </div>
      </div>

      {/* Privacy Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Shield className="w-6 h-6 text-purple-600" />
          <h2 className="text-lg font-semibold text-gray-900">Privacy & Security</h2>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Profile Visibility</label>
            <select
              value={settings.privacy.profileVisibility}
              onChange={(e) => updateSettings('privacy', 'profileVisibility', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="public">Public</option>
              <option value="private">Private</option>
              <option value="friends">Friends Only</option>
            </select>
          </div>

          <div className="space-y-4">
            {Object.entries(settings.privacy).filter(([key]) => key !== 'profileVisibility').map(([key, value]) => (
              <div key={key} className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-gray-900">
                    {key.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase())}
                  </div>
                  <div className="text-sm text-gray-600">
                    {key === 'showEarnings' && 'Allow others to see your earnings'}
                    {key === 'showReferrals' && 'Allow others to see your referrals'}
                    {key === 'allowDirectMessages' && 'Allow others to send you direct messages'}
                    {key === 'dataSharing' && 'Allow data sharing for analytics'}
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={value}
                    onChange={(e) => updateSettings('privacy', key, e.target.checked)}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                </label>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Payout Settings */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <CreditCard className="w-6 h-6 text-orange-600" />
          <h2 className="text-lg font-semibold text-gray-900">Payout Settings</h2>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Preferred Payout Method</label>
            <select
              value={settings.payout.preferredMethod}
              onChange={(e) => updateSettings('payout', 'preferredMethod', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="gcash">GCash</option>
              <option value="paymaya">PayMaya</option>
              <option value="bank">Bank Transfer</option>
            </select>
          </div>

          {settings.payout.preferredMethod === 'gcash' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">GCash Number</label>
              <input
                type="tel"
                value={settings.payout.gcashNumber}
                onChange={(e) => updateSettings('payout', 'gcashNumber', e.target.value)}
                placeholder="09123456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {settings.payout.preferredMethod === 'paymaya' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">PayMaya Number</label>
              <input
                type="tel"
                value={settings.payout.paymayaNumber}
                onChange={(e) => updateSettings('payout', 'paymayaNumber', e.target.value)}
                placeholder="09123456789"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          )}

          {settings.payout.preferredMethod === 'bank' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name</label>
                <input
                  type="text"
                  value={settings.payout.bankName}
                  onChange={(e) => updateSettings('payout', 'bankName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Number</label>
                <input
                  type="text"
                  value={settings.payout.bankAccount}
                  onChange={(e) => updateSettings('payout', 'bankAccount', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Account Name</label>
                <input
                  type="text"
                  value={settings.payout.accountName}
                  onChange={(e) => updateSettings('payout', 'accountName', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Payout Amount</label>
              <input
                type="number"
                step="0.01"
                value={settings.payout.minimumPayout}
                onChange={(e) => updateSettings('payout', 'minimumPayout', parseFloat(e.target.value))}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex items-center space-x-3">
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={settings.payout.autoPayout}
                  onChange={(e) => updateSettings('payout', 'autoPayout', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
              <div>
                <div className="font-medium text-gray-900">Auto Payout</div>
                <div className="text-sm text-gray-600">Automatically request payout when minimum is reached</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Preferences */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <Globe className="w-6 h-6 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">Preferences</h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
            <select
              value={settings.preferences.timezone}
              onChange={(e) => updateSettings('preferences', 'timezone', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="Asia/Manila">Asia/Manila (GMT+8)</option>
              <option value="UTC">UTC (GMT+0)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Currency</label>
            <select
              value={settings.preferences.currency}
              onChange={(e) => updateSettings('preferences', 'currency', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="PHP">Philippine Peso (PHP)</option>
              <option value="USD">US Dollar (USD)</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Language</label>
            <select
              value={settings.preferences.language}
              onChange={(e) => updateSettings('preferences', 'language', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="en">English</option>
              <option value="fil">Filipino</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Theme</label>
            <select
              value={settings.preferences.theme}
              onChange={(e) => updateSettings('preferences', 'theme', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="light">Light</option>
              <option value="dark">Dark</option>
              <option value="auto">Auto</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
}
